/**
 * The Strip Card setup step of the create-pool dialog (ISSUE-1287), mounted
 * by `features/createPool/CreatePoolTypeSetupStep.tsx` with this exact props
 * shape. After v1's strip_card_setup.dart: the first game (a day, then a
 * game on that day) and max spots per member. The league is the one the fan
 * picked a step earlier; its id comes from the league catalog.
 */
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Input, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  STRIP_CUSTOM_GAME_NAME_LABEL,
  STRIP_CUSTOM_GAME_NAME_MAX_LENGTH,
  STRIP_CUSTOM_GAME_NAME_PLACEHOLDER,
  STRIP_SETUP_CUSTOM_FIRST_GAME_HINT,
  STRIP_SETUP_CUSTOM_GAME_NAME_HINT,
  STRIP_SETUP_FIRST_GAME_HEADING,
  STRIP_SETUP_FIRST_GAME_HINT,
  STRIP_SETUP_NO_LEAGUE,
  stripMaxSpotsSummary,
} from "./stripCardCopy";
import type { typeStripCardSetupDraft } from "./stripCardCreateSetupDraft";
import { StripGamePicker } from "./StripGamePicker";
import { StripMaxSpotsField } from "./StripMaxSpotsField";

export type typeStripCardCreateSetupStepProps = {
  sportKey: string | null;
  leagueKey: string | null;
  setup: typeStripCardSetupDraft;
  onChangeSetup: (next: typeStripCardSetupDraft) => void;
};

export const StripCardCreateSetupStep = (
  props: typeStripCardCreateSetupStepProps,
): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const leagueId =
    props.leagueKey === null
      ? null
      : (catalog.data?.data?.sports
          .flatMap((sport) => sport.leagues)
          .find((league) => league.leagueKey === props.leagueKey)?.leagueId ??
        null);

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="strip-card-create-setup-step"
      data-league-key={props.leagueKey ?? ""}
    >
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-body-strong text-ink">
            {STRIP_SETUP_FIRST_GAME_HEADING}
          </h3>
          <p className="text-caption text-ink-secondary">
            {props.setup.isCustomLeague
              ? STRIP_SETUP_CUSTOM_FIRST_GAME_HINT
              : STRIP_SETUP_FIRST_GAME_HINT}
          </p>
        </div>
        {props.setup.isCustomLeague ? (
          // ISSUE-1299. No league, no schedule: the first game is a custom
          // game the Commissioner names here (or leaves blank).
          <Input
            label={STRIP_CUSTOM_GAME_NAME_LABEL}
            hint={STRIP_SETUP_CUSTOM_GAME_NAME_HINT}
            placeholder={STRIP_CUSTOM_GAME_NAME_PLACEHOLDER}
            maxLength={STRIP_CUSTOM_GAME_NAME_MAX_LENGTH}
            value={props.setup.customGameTitle}
            data-testid="strip-setup-custom-game-name"
            onChange={(event) => {
              props.onChangeSetup({
                ...props.setup,
                customGameTitle: event.target.value,
              });
            }}
          />
        ) : leagueId !== null ? (
          <StripGamePicker
            leagueId={leagueId}
            value={{ day: props.setup.day, eventId: props.setup.eventId }}
            onChange={(pick) => {
              props.onChangeSetup({
                ...props.setup,
                day: pick.day,
                eventId: pick.eventId,
                gameLabel: pick.gameLabel ?? null,
              });
            }}
          />
        ) : catalog.isPending && props.leagueKey !== null ? (
          <Skeleton shape="card" />
        ) : (
          <p
            className="text-body text-ink-secondary"
            data-testid="strip-setup-no-league"
          >
            {STRIP_SETUP_NO_LEAGUE}
          </p>
        )}
      </section>
      <section className="flex flex-col gap-2">
        <StripMaxSpotsField
          value={props.setup.maxSpotsPerMember}
          onChange={(maxSpotsPerMember) => {
            props.onChangeSetup({ ...props.setup, maxSpotsPerMember });
          }}
        />
        <p className="text-caption text-ink-secondary">
          {stripMaxSpotsSummary(props.setup.maxSpotsPerMember)}
        </p>
      </section>
    </div>
  );
};
